import React, { useImperativeHandle, useMemo } from 'react';
import { TourManager } from './context/TourContext';
import type { TourContextValue } from './context/TourContext';
import type { TourProviderProps, TourProviderRef } from './Tour.types';
import { TourStepDisplay } from './components/TourStepDisplay';
import { useTourStepRegistry } from './hooks/useTourStepRegistry';
import { useTourNavigation } from './hooks/useTourNavigation';
import { useTourKeyboardNavigation } from './hooks/useTourKeyboardNavigation';
import { useTourBodyScrollLock } from './hooks/useTourBodyScrollLock';
import { TourTelemetryProvider } from './Tour.telemetry';

/**
 * TourProvider - Generic tour orchestration component
 *
 * Features:
 * - Self-discovery: Steps register themselves dynamically
 * - Imperative API: Expose run() via ref
 * - Step ordering: Custom order via stepOrder prop
 */
const TourRoot = React.memo(
  React.forwardRef<TourProviderRef, TourProviderProps>(({ tourName, stepOrder, onComplete, children }, ref) => {
    // Step registry: manages step registration and validation
    const { steps, expectedStepNames, validExpectedSteps, registerStep, unregisterStep } = useTourStepRegistry(tourName, stepOrder);

    // Navigation: manages tour state and navigation actions
    const { isOpen, currentStepIndex, currentStepName, hasNext, hasPrevious, isCollapsed, run, stop, next, prev, setIsCollapsed } = useTourNavigation({
      tourName,
      expectedStepNames,
      validExpectedSteps,
      onComplete,
    });

    // Expose imperative API via ref
    useImperativeHandle(
      ref,
      () => ({
        run,
        stop,
        isOpen,
      }),
      [run, stop, isOpen],
    );

    // Keyboard navigation
    useTourKeyboardNavigation({
      isOpen,
      hasNext,
      hasPrevious,
      onStop: stop,
      onNext: next,
      onPrevious: prev,
    });

    // Body scroll lock
    useTourBodyScrollLock(isOpen);

    const contextValue: TourContextValue = useMemo(
      () => ({
        tourName,
        steps, // Use state version for reactivity
        registerStep,
        unregisterStep,
        isOpen,
        currentStepName,
        currentStepIndex,
        orderedStepNames: expectedStepNames,
        isCollapsed,
        run,
        stop,
        next,
        prev,
        setIsCollapsed,
        hasNext,
        hasPrevious,
      }),
      [
        tourName,
        steps,
        isOpen,
        currentStepName,
        currentStepIndex,
        expectedStepNames,
        isCollapsed,
        hasNext,
        hasPrevious,
        registerStep,
        unregisterStep,
        run,
        stop,
        next,
        prev,
        setIsCollapsed,
      ],
    );

    return (
      <TourManager value={contextValue}>
        {children}
        <TourStepDisplay />
      </TourManager>
    );
  }),
);

/**
 * Root component that safely wraps TourTelemetryProvider
 */
const TourProvider = React.memo(
  React.forwardRef<TourProviderRef, TourProviderProps>((props, ref) => {
    return (
      <TourTelemetryProvider>
        <TourRoot
          ref={ref}
          tourName={props.tourName}
          stepOrder={props.stepOrder}
          onComplete={props.onComplete}
        >
          {props.children}
        </TourRoot>
      </TourTelemetryProvider>
    );
  }),
);

TourRoot.displayName = 'TourRoot';
TourProvider.displayName = 'TourProvider';

export { TourProvider };
